import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SimulationGroup } from '@thive/fddm-types';

import { useDialog } from '@/modules/common/hooks';
import { TopButtons } from '@/modules/simulation/components/TopButtons';
import { DeleteConfirmDialog } from '@/modules/simulation/components/ConfirmDialog';
import { Status } from '@/modules/simulation/components/Status';
import { SimulationUtil } from '@/modules/simulation/SimulationUtil';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';

import { GroupTopButtons } from './GroupTopButtons';

type Props = {
  simulationGroup: SimulationGroup;
  isHover: boolean;
  onClick: () => void;
  onDetails: (simulationGroup: SimulationGroup) => void;
  onEdit: (simulation: SimulationGroup) => void;
  onRerun: (simulation: SimulationGroup) => void;
  onDelete: (simulation: SimulationGroup) => void;
};

export const CardHeader = ({
  simulationGroup,
  isHover,
  onClick,
  onDetails,
  onEdit,
  onRerun,
  onDelete,
}: Props) => {
  const { open: deleteDialogOpen, setOpen: toggleDeleteDialog } = useDialog();
  const navigate = useNavigate();
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );

  const [message, setMessage] = useState<string | undefined>(undefined);

  const getMessage = useCallback(
    async () => SimulationUtil.getErrorMessage(simulationGroup),
    [simulationGroup],
  );

  const onDeleteConfirm = useCallback(async () => {
    onDelete(simulationGroup);
  }, [onDelete, simulationGroup]);

  const onEditClick = useCallback(() => onEdit(simulationGroup), [onEdit, simulationGroup]);
  const onRerunClick = useCallback(() => onRerun(simulationGroup), [onRerun, simulationGroup]);

  // should this be moved to gotomodule
  // TODO: This beta parameter should be removed
  const goToTracker = useCallback(
    (isBeta: boolean) => {
      const floorPlanMetaData = useFloorplanDesignStore.getState().metaData;

      if (!simulationGroup.floorPlanRevisionId) {
        return;
      }

      const url = SimulationUtil.createFleetTrackerUrl(
        floorPlanMetaData.projectId,
        floorPlanMetaData.alternativeId,
        floorPlanMetaData.revisionId,
        simulationGroup.simulationRunId,
        simulationGroup.floorPlanRevisionId,
        isBeta,
      );
      navigate(url);
    },
    [simulationGroup.simulationRunId, simulationGroup.floorPlanRevisionId, navigate],
  );

  const onDetailsClick = useCallback(
    async () => onDetails(simulationGroup),
    [onDetails, simulationGroup],
  );

  const onDeleteClick = useCallback(() => toggleDeleteDialog(true), [toggleDeleteDialog]);

  const onDownloadSimulationFilesClick = useCallback(() => {
    if (simulationGroup.simulationRunId) {
      simSvc.downloadSimulationFiles(simulationGroup.simulationRunId, simulationGroup.name);
    }
  }, [simulationGroup.simulationRunId, simulationGroup.name, simSvc]);

  useEffect(() => {
    const fetchMessage = async () => {
      setMessage(await SimulationUtil.getErrorMessage(simulationGroup));
    };
    fetchMessage();
  }, [getMessage, simulationGroup]);

  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: 1,
        mb: 1,
        justifyContent: 'space-between',
      }}
    >
      <Stack
        sx={{ flexBasis: '100%', alignItems: 'center', flexDirection: 'row', height: '100%' }}
        onClick={onClick}
      >
        <Status
          status={simulationGroup.status}
          isGroup={SimulationUtil.isGroup(simulationGroup)}
          message={message}
        />
      </Stack>
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexDirection: 'row',
          gap: 2,
          height: '32px',
          flexBasis: '50px',
        }}
      >
        {isHover &&
          (SimulationUtil.isGroup(simulationGroup) ? (
            <GroupTopButtons
              simulationGroup={simulationGroup}
              onRerunClick={onRerunClick}
              onEditClick={onEditClick}
              onDetailsClick={onDetailsClick}
              onDeleteClick={onDeleteClick}
            />
          ) : (
            <TopButtons
              status={simulationGroup.status}
              simulationRunId={simulationGroup.simulationRunId}
              dashboardUrl={simulationGroup.dashboardUrl}
              dashboardUrlExpirationDate={
                simulationGroup.dashboardUrlExpirationDate
                  ? new Date(simulationGroup.dashboardUrlExpirationDate)
                  : null
              }
              onViewClick={() => goToTracker(true)}
              onRerunClick={onRerunClick}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
              onDownloadSimulationFilesClick={onDownloadSimulationFilesClick}
            />
          ))}
      </Stack>
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        runName={simulationGroup.name}
        onConfirm={() => {
          onDeleteConfirm();
          toggleDeleteDialog(false);
        }}
        onCancel={() => toggleDeleteDialog(false)}
      />
    </Stack>
  );
};
